import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { StepsModule } from 'primeng/steps';
import { StepperModule } from 'primeng/stepper';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageModule } from 'primeng/message';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { MultiSelectModule } from 'primeng/multiselect';
import { Textarea } from 'primeng/textarea';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { WorkflowNavigationService } from '@workspace-app/shared/services/workflow-navigation.service';
// Application imports
import { PageComponent, ToastService, User } from '@workspace-app/shared';
import { selectUser } from '@workspace-app/state/global';
import { PermissionService } from '@workspace-app/shared/services/permission.service';
import {
  ActionMenuItem,
  ActionsMenuComponent,
} from '@workspace-app/shared/components/actions-menu/actions-menu.component';
import {
  CustomerOrganisation,
  InspectionSummaryDto,
  LicenseRenewalCandidates,
  WorkflowDocument,
} from '@workspace-app/odatabackend/QualicoatBackendModel';
import { DocumentService } from '@workspace-app/shared/services/document.service';
import { RenewalTimelineDialogComponent } from '../../components/renewal-timeline-dialog/renewal-timeline-dialog.component';
import { LicenseRenewalWorkflowStore } from '../../state/license-renewal-workflow.store';
import {
  RenewedCertificate,
  RenewedCertificatesTableComponent,
} from '../../components/renewed-certificates-table/renewed-certificates-table.component';
import {
  ProcessedLicense,
  ProcessedLicensesCardsComponent,
} from '../../components/processed-licenses-cards/processed-licenses-cards.component';

@Component({
  selector: 'app-license-extension-review-workflow',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    PageComponent,
    ButtonModule,
    StepsModule,
    StepperModule,
    CardModule,
    TableModule,
    PaginatorModule,
    CheckboxModule,
    DialogModule,
    TagModule,
    TooltipModule,
    ProgressBarModule,
    MessageModule,
    ChipModule,
    DividerModule,
    MultiSelectModule,
    ActionsMenuComponent,
    FaIconComponent,
    RenewedCertificatesTableComponent,
    ProcessedLicensesCardsComponent,
    Textarea,
    ConfirmDialogModule,
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: './license-renewal-workflow.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./license-renewal-workflow.component.scss'],
})
export class LicenseRenewalWorkflowWorkflowComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private permissionService = inject(PermissionService);
  private workflowNavigation = inject(WorkflowNavigationService);
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private dialogService = inject(DialogService);
  private confirmationService = inject(ConfirmationService);
  private documentService = inject(DocumentService);
  private odataFactory = inject(ODataServiceFactory);
  private workflowDocumentService: ODataEntitySetService<WorkflowDocument>;

  // Dialog references
  private timelineRef: DynamicDialogRef | null = null;

  licenseStore = inject(LicenseRenewalWorkflowStore);
  currentUser$: Observable<User>;

  reviewId = signal<string | null>(null);

  // Track current page candidates for pagination
  currentPageCandidates = signal<LicenseRenewalCandidates[]>([]);
  currentPageFirstIndex = signal<number>(0);
  currentPageRows = signal<number>(10);

  // Simplified workflow steps
  steps: MenuItem[] = [
    { label: 'Candidates Selection', icon: 'pi pi-search' },
    { label: 'GL Notification', icon: 'pi pi-envelope' },
    { label: 'Create RN1 Inspections', icon: 'pi pi-plus-circle' },
    { label: 'Finalize Review', icon: 'pi pi-flag-fill' },
  ];

  activeIndex = computed(() => {
    const review = this.licenseStore.activeReview();
    if (!review) return 0;

    // Map backend states to simplified workflow steps
    const stateToStepIndex: { [key: string]: number } = {
      Created: 0, // Step 1: Candidates Selection (initial)
      Review: 0, // Step 1: Candidates Selection (reviewing)
      IssuingCertificates: 1, // Step 2: GL Notification
      ReadyToCreateRn1: 2, // Step 3: Create RN1 Inspections
      FinalReview: 3, // Step 4: Finalize Review
      Completed: 3, // Step 4: Finalize Review (completed)
      Cancelled: -1, // Not in workflow
    };

    const stepIndex = stateToStepIndex[review.currentState] ?? 0;
    return Math.max(0, Math.min(stepIndex, this.steps.length - 1));
  });

  eligibleCandidatesCount = computed(() => {
    const candidates = this.licenseStore.availableCandidates();
    return candidates.filter((c) => c.isEligible).length;
  });

  extendedCandidatesCount = computed(() => {
    const candidates = this.licenseStore.availableCandidates();
    return candidates.filter((c) => c.wasExtended).length;
  });

  allEligibleSelected = computed(() => {
    const candidates = this.licenseStore.availableCandidates();
    const selectedIds = this.licenseStore.selectedCandidateIds();
    const eligibleCandidates = candidates.filter((c) => c.isEligible);
    return eligibleCandidates.length > 0 && eligibleCandidates.every((c) => selectedIds.includes(c.id));
  });

  someEligibleSelected = computed(() => {
    const candidates = this.licenseStore.availableCandidates();
    const selectedIds = this.licenseStore.selectedCandidateIds();
    const eligibleCandidates = candidates.filter((c) => c.isEligible);
    return selectedIds.length > 0 && selectedIds.length < eligibleCandidates.length;
  });

  // Forms
  confirmForm!: FormGroup;
  completeForm!: FormGroup;
  cancelForm!: FormGroup;

  // UI state
  showConfirmDialogVisible = false;
  showCancelDialog = signal(false);

  // Form data
  finalReviewNotes = '';

  // GL notification state
  selectedCertificates: RenewedCertificate[] = [];

  // Permission state
  currentUser = signal<User | undefined>(undefined);

  // Computed renewed certificates from confirmed candidates (has GL contact info) or selected candidates
  renewedCertificates = computed(() => {
    const confirmedCandidates = this.licenseStore.confirmedCandidates();
    const selectedCandidates = this.licenseStore.selectedCandidates();

    // Use confirmed candidates if available (has GL contact info)
    if (confirmedCandidates.length > 0) {
      return confirmedCandidates.map((c): RenewedCertificate => {
        // Find matching selected candidate for certificate/notification status (more up-to-date)
        const selected = selectedCandidates.find((s) => s.id === c.id);

        return {
          id: c.id,
          licenseId: c.licenseId,
          licenseNumber: c.licenseNumber || 'N/A',
          coaterName: c.coaterName,
          productionSiteName: c.productionSiteName,
          city: c.city,
          currentExpiryDate: c.currentExpiryDate ? new Date(c.currentExpiryDate) : undefined,
          newExpiryDate: c.newExpiryDate ? new Date(c.newExpiryDate) : undefined,
          wasExtended: c.wasExtended,
          certificateSent: selected?.certificateSent || c.certificateSent || false,
          certificateSentOn: selected?.certificateSentOn
            ? new Date(selected.certificateSentOn)
            : c.certificateSentOn
              ? new Date(c.certificateSentOn)
              : undefined,
          certificateGenerationStatus: selected?.certificateGenerationStatus || undefined,
          glNotificationStatus: selected?.glNotificationStatus || undefined,
          glName: c.glName,
          glEmail: c.glContactEmail,
          selected: false,
        };
      });
    }

    // Fallback to selected candidates (no GL contact info) - only if confirmedCandidates truly has no data
    return selectedCandidates
      .filter((c) => c.wasExtended)
      .map((c): RenewedCertificate => {
        const customerName = c.license?.productionSite?.customer?.name || '';

        return {
          id: c.id,
          licenseId: c.licenseId,
          licenseNumber: c.license?.completeLicenseNumber || 'N/A',
          coaterName: customerName,
          productionSiteName: c.license?.productionSite?.name,
          city: c.license?.productionSite?.city,
          currentExpiryDate: c.originalExpiryDate ? new Date(c.originalExpiryDate) : undefined,
          newExpiryDate: c.newExpiryDate ? new Date(c.newExpiryDate) : undefined,
          wasExtended: c.wasExtended,
          certificateSent: c.certificateSent || false,
          certificateSentOn: c.certificateSentOn ? new Date(c.certificateSentOn) : undefined,
          certificateGenerationStatus: c.certificateGenerationStatus || undefined,
          glNotificationStatus: c.glNotificationStatus || undefined,
          glName: (c.license?.productionSite?.customer as CustomerOrganisation)?.assignedGl?.name,
          glEmail: undefined,
          selected: false,
        };
      });
  });

  // Computed properties
  canSelectCandidates = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    return review?.currentState === 'Created' && this.permissionService.hasEffectivePermission(user, 'Workflow.Write');
  });

  canUpdateSelection = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    return review?.currentState === 'Review' && this.permissionService.hasEffectivePermission(user, 'Workflow.Write');
  });

  canConfirmSelection = computed(() => {
    const review = this.licenseStore.activeReview();
    const hasSelection = this.licenseStore.selectedCandidateIds().length > 0;
    const user = this.currentUser();
    return (
      review?.currentState === 'Review' &&
      hasSelection &&
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write')
    );
  });

  canNotifyGLs = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    return (
      (review?.currentState === 'IssuingCertificates' || review?.currentState === 'FinalReview') &&
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write')
    );
  });

  canComplete = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    return (
      (review?.currentState === 'IssuingCertificates' || review?.currentState === 'FinalReview') &&
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write')
    );
  });

  canCancel = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    // Only allow cancel in step 1 (Created or Review states)
    return (
      review?.isActive &&
      (review?.currentState === 'Created' || review?.currentState === 'Review') &&
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write')
    );
  });

  // Check if user is super admin
  isUserSuperAdmin(user: User | null | undefined): boolean {
    if (!user) return false;
    return this.permissionService.hasEffectiveRole(user || undefined, 'SuperAdmin');
  }

  // Action menu items
  actionMenuItems = computed<ActionMenuItem[]>(() => {
    const items: ActionMenuItem[] = [];

    if (this.canCancel()) {
      items.push({
        label: 'Cancel Review',
        icon: 'pi pi-times',
        danger: true,
        command: () => this.openCancelDialog(),
      });
    }

    return items;
  });

  // Progress percentage - properly computed based on state
  progressPercentage = computed(() => {
    const review = this.licenseStore.activeReview();
    if (!review) return 0;

    const stateProgress: { [key: string]: number } = {
      Created: 0, // Step 1 - Not started
      Review: 33, // Step 1 - In Candidates Selection
      IssuingCertificates: 50, // Step 2 - Ready for Certificate Issuing
      FinalReview: 66, // Step 3 - Final Review in progress
      Completed: 100, // Step 3 - All steps done
      Cancelled: 0,
    };

    return stateProgress[review.currentState] || 0;
  });
  constructor() {
    this.currentUser$ = this.store.select(selectUser);
    this.workflowDocumentService = this.odataFactory.entitySet<WorkflowDocument>('WorkflowDocuments');

    // Effect to update current page candidates when candidates list changes
    effect(() => {
      const allCandidates = this.licenseStore.availableCandidates();
      const firstIndex = this.currentPageFirstIndex();
      const rows = this.currentPageRows();

      // Calculate current page slice
      const currentPage = allCandidates.slice(firstIndex, firstIndex + rows);
      this.currentPageCandidates.set(currentPage);
    });
  }

  ngOnInit(): void {
    this.initializeForms();

    // Subscribe to user changes
    this.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.currentUser.set(user);
    });

    // Get review ID from route
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.reviewId.set(id);
        this.loadReview(id);

        // Note: Certificate status is loaded by store's onInit() for IssuingCertificates/FinalReview states
        // and by loadReview() for Completed state. No need to load unconditionally here.

        // Check if we're the starting point of navigation (not coming from anywhere in workflow)
        const currentPath = `/certification/licenses/extension-reviews/${id}`;
        const currentNode = this.workflowNavigation.getCurrentNode();
        const isStartingPoint =
          !this.workflowNavigation.hasNavigationHistory() ||
          (currentNode && currentNode.path === currentPath && this.workflowNavigation.getStackDepth() === 1);

        if (isStartingPoint) {
          // Clear the workflow stack if we're the starting point
          this.workflowNavigation.clearStack();
        } else if (this.workflowNavigation.hasNavigationHistory()) {
          // We're part of an existing workflow, add/update ourselves
          setTimeout(() => {
            const review = this.licenseStore.activeReview();
            if (review) {
              this.workflowNavigation.addOrUpdateCurrentNode({
                path: currentPath,
                data: {
                  component: 'license-extension-review-workflow',
                  entityType: 'review',
                  entityId: review.id,
                  displayName: `License Renewal - ${review.reviewNumber}`,
                  workflowStep: this.licenseStore.currentStep(),
                },
              });
            }
          }, 500);
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Clear the active review from the store to prevent stale data
    // when navigating to another review
    this.licenseStore.resetStore();

    // Close any open dialogs
    if (this.timelineRef) {
      this.timelineRef.close();
    }
  }

  initializeForms(): void {
    this.confirmForm = this.fb.group({
      notes: [''],
    });

    this.cancelForm = this.fb.group({
      reason: ['', Validators.required],
    });
  }

  loadReview(id: string): void {
    this.licenseStore.loadReview(id);

    // Load state-specific data after review loads
    setTimeout(() => {
      const review = this.licenseStore.activeReview();
      if (!review || review.currentState === 'Created' || review.currentState === 'Cancelled') {
        return;
      }

      // Step 1 (Review state): Load all available candidates for selection
      if (review.currentState === 'Review' || review.currentState === 'CandidatesIdentified') {
        this.loadCandidates();
      }

      // Step 2+ (IssuingCertificates/ReadyToCreateRn1/FinalReview/Completed): Load selected candidates
      if (
        review.currentState === 'IssuingCertificates' ||
        review.currentState === 'ReadyToCreateRn1' ||
        review.currentState === 'FinalReview' ||
        review.currentState === 'Completed'
      ) {
        this.licenseStore.loadCertificateGenerationStatus(id);
        this.licenseStore.loadSelectedCandidates(id);
        this.licenseStore.loadConfirmedCandidates(id);
      }
    }, 500);
  }

  loadCandidates(): void {
    const reviewId = this.reviewId();
    if (reviewId) {
      this.licenseStore.loadCandidates(reviewId);
    }
  }

  // Workflow actions
  selectCandidates(): void {
    this.licenseStore.selectCandidates();
  }

  toggleCandidateSelection(candidate: LicenseRenewalCandidates): void {
    const selectedIds = this.licenseStore.selectedCandidateIds();
    const isSelected = selectedIds.includes(candidate.id);

    this.licenseStore.updateSelection({
      candidateId: candidate.id,
      isSelected: !isSelected,
    });
  }

  isCandidateSelected(candidate: LicenseRenewalCandidates): boolean {
    const selectedIds = this.licenseStore.selectedCandidateIds();
    return selectedIds.includes(candidate.id);
  }

  areAllCandidatesOnPageSelected(): boolean {
    const candidates = this.currentPageCandidates();
    const selectedIds = this.licenseStore.selectedCandidateIds();

    if (candidates.length === 0) {
      return false;
    }

    // Check if all candidates on current page are selected
    return candidates.every((c) => selectedIds.includes(c.id));
  }

  toggleAllCandidatesOnPage(event: { checked?: boolean }): void {
    const candidates = this.currentPageCandidates();
    const isSelected = event.checked ?? false;

    // Update selection for all candidates on current page only
    candidates.forEach((candidate) => {
      this.licenseStore.updateSelection({
        candidateId: candidate.id,
        isSelected: isSelected,
      });
    });
  }

  onCandidatesPageChange(event: { first: number; rows: number }): void {
    // Update pagination state
    this.currentPageFirstIndex.set(event.first);
    this.currentPageRows.set(event.rows);
  }

  selectAllEligible(): void {
    const candidates = this.licenseStore.availableCandidates();

    // Update store for each candidate
    candidates.forEach((c) => {
      this.licenseStore.updateSelection({
        candidateId: c.id,
        isSelected: c.isEligible,
      });
    });
  }

  clearSelection(): void {
    const candidates = this.licenseStore.availableCandidates();

    // Update store for each candidate
    candidates.forEach((c) => {
      this.licenseStore.updateSelection({
        candidateId: c.id,
        isSelected: false,
      });
    });
  }

  saveSelection(): void {
    // Only used in Step 1 (Review state) to save candidate selection
    this.licenseStore.saveSelection();
  }

  refreshInspections(): void {
    // Refresh inspection data for all candidates
    this.licenseStore.refreshInspections();
  }
  openConfirmDialog(): void {
    this.showConfirmDialogVisible = true;
  }

  confirmSelection(): void {
    const notes = this.confirmForm.value.notes || undefined;
    this.licenseStore.confirmSelection(notes);
    this.showConfirmDialogVisible = false;
    this.confirmForm.reset();
  }

  // Handle certificate selection changes from table
  onCertificateSelectionChanged(certificates: RenewedCertificate[]): void {
    this.selectedCertificates = certificates;
  }

  // Notify GLs about new certificates
  notifyGLs(): void {
    if (this.selectedCertificates.length === 0) {
      this.toastService.displayErrorMessage('Please select at least one certificate to notify GLs.');
      return;
    }

    const licenseIds = this.selectedCertificates.map((c) => c.licenseId);

    // Call the store method to notify GLs
    this.licenseStore.notifyGLs(licenseIds);
  }

  skipGLNotification(): void {
    // Skip GL notification and proceed directly to finalization
    // Pass an empty array to indicate no GLs should be notified
    this.licenseStore.notifyGLs([]);

    // Optionally show a message that GL notification was skipped
    this.toastService.displaySuccessMessage('GL notification skipped. Proceeding to finalize review.');
  }

  createRn1Inspections(): void {
    // Call the store method to create RN1 inspections for next year
    this.licenseStore.createRn1Inspections();
  }

  canCreateRn1Inspections = computed(() => {
    const review = this.licenseStore.activeReview();
    const user = this.currentUser();
    return (
      review?.currentState === 'ReadyToCreateRn1' &&
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write')
    );
  });

  skipRn1Creation(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to skip RN1 inspection creation? You can create them manually later if needed.',
      header: 'Skip RN1 Creation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-secondary',
      acceptLabel: 'Skip',
      rejectLabel: 'Cancel',
      accept: () => {
        this.licenseStore.skipRn1Creation('RN1 inspection creation skipped');
      },
    });
  }

  finalizeReview(): void {
    const notes = this.finalReviewNotes || undefined;
    this.licenseStore.completeReview(notes);

    // Reset the notes field
    this.finalReviewNotes = '';

    // Navigate back to list after completion
    setTimeout(() => {
      this.router.navigate(['/certification/licenses/extension-reviews']);
    }, 1000);
  }

  openCancelDialog(): void {
    this.showCancelDialog.set(true);
  }

  cancelReview(): void {
    if (this.cancelForm.valid) {
      const reason = this.cancelForm.value.reason;
      this.licenseStore.cancelReview(reason);
      this.showCancelDialog.set(false);
      this.cancelForm.reset();

      // Navigate back to list after cancellation
      setTimeout(() => {
        this.router.navigate(['/certification/licenses/extension-reviews']);
      }, 1000);
    }
  }

  // Helper method to parse all inspections from JSON
  getAllInspections(candidate: LicenseRenewalCandidates): InspectionSummaryDto[] {
    // Now allInspections is properly deserialized by OData
    // Sort by inspection date (oldest first)
    const inspections = candidate.allInspections || [];
    return inspections.sort((a, b) => {
      if (!a.inspectionDate || !b.inspectionDate) return 0;
      return new Date(a.inspectionDate).getTime() - new Date(b.inspectionDate).getTime();
    });
  }

  // Helper to format inspection type with certification year if different from review year
  formatInspectionType(inspection: InspectionSummaryDto): string {
    const review = this.licenseStore.activeReview();
    if (!review) return inspection.inspectionTypeCode;

    const reviewYear = review.certificationYear;
    const inspectionYear = inspection.certificationYear;

    // Show certification year in parenthesis if different from review year
    if (inspectionYear && inspectionYear !== reviewYear) {
      return `${inspection.inspectionTypeCode} (${inspectionYear})`;
    }

    return inspection.inspectionTypeCode;
  }

  // Helper to group inspections by type
  getInspectionsByType(candidate: LicenseRenewalCandidates): Map<string, InspectionSummaryDto[]> {
    const inspections = this.getAllInspections(candidate);
    const grouped = new Map<string, InspectionSummaryDto[]>();

    inspections.forEach((inspection) => {
      const type = inspection.inspectionTypeCode;
      if (!grouped.has(type)) {
        grouped.set(type, []);
      }
      grouped.get(type)!.push(inspection);
    });

    return grouped;
  }

  // Navigation
  navigateToInspection(inspectionId: string | null | undefined): void {
    if (!inspectionId) {
      console.warn('No inspection ID provided');
      return;
    }

    // Ensure the License Review is in the workflow stack before navigating
    const review = this.licenseStore.activeReview();
    if (review && !this.workflowNavigation.hasNavigationHistory()) {
      // Add the License Review to the workflow stack if not already there
      this.workflowNavigation.addOrUpdateCurrentNode({
        path: `/certification/licenses/extension-reviews/${review.id}`,
        data: {
          component: 'license-extension-review-workflow',
          entityType: 'review',
          entityId: review.id,
          displayName: `License Renewal - ${review.reviewNumber}`,
          workflowStep: this.licenseStore.currentStep(),
        },
      });
    }

    // Navigate to the inspection - the inspection component will handle adding itself to the workflow
    // Pass workflow context in navigation state
    this.router.navigate(['/inspections/coaters', inspectionId], {
      state: { fromWorkflow: true },
    });
  }

  // Helpers
  getStateLabel(state: string): string {
    const labels: { [key: string]: string } = {
      Created: 'Review Created',
      Review: 'In Review',
      IssuingCertificates: 'Ready to Issue Certificates',
      FinalReview: 'Final Review',
      Completed: 'Review Completed',
      Cancelled: 'Review Cancelled',
    };
    return labels[state] || state;
  }

  getStateSeverity(state: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | undefined {
    const severities: { [key: string]: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | undefined } = {
      Created: 'info',
      Review: 'warn',
      IssuingCertificates: 'info',
      FinalReview: 'warn',
      Completed: 'success',
      Cancelled: 'danger',
    };
    return severities[state] || 'secondary';
  }

  getEligibilitySeverity(isEligible: boolean): 'success' | 'warn' {
    return isEligible ? 'success' : 'warn';
  }

  getEligibilityLabel(isEligible: boolean): string {
    return isEligible ? 'Eligible' : 'Review Required';
  }

  // Processed licenses methods
  showProcessedLicenses = computed(() => {
    const state = this.licenseStore.activeReview()?.currentState;
    return state === 'FinalReview' || state === 'Completed';
  });

  processedLicensesForDisplay = computed(() => {
    const candidates = this.licenseStore.selectedCandidates();
    const confirmedCandidates = this.licenseStore.confirmedCandidates();

    return candidates.map((c): ProcessedLicense => {
      const customerName = c.license?.productionSite?.customer?.name || '';
      const productionSiteName = c.license?.productionSite?.name || '';
      const coaterName =
        customerName && productionSiteName
          ? `${customerName} - ${productionSiteName}`
          : productionSiteName || customerName || 'Unknown';

      const gl = (c.license?.productionSite?.customer as CustomerOrganisation)?.assignedGl;

      // Get GL contact info from confirmedCandidates (has email info from API)
      const confirmed = confirmedCandidates.find((cc) => cc.id === c.id);

      return {
        id: c.id,
        licenseId: c.licenseId,
        licenseNumber: c.license?.completeLicenseNumber || 'N/A',
        coaterName,
        productionSiteName: c.license?.productionSite?.name || 'Unknown',
        city: c.license?.productionSite?.city || '',
        currentExpiryDate: c.originalExpiryDate ? new Date(c.originalExpiryDate) : new Date(),
        newExpiryDate: c.newExpiryDate ? new Date(c.newExpiryDate) : new Date(),
        certificateStatus: c.certificateGenerationStatus || 'Pending',
        certificateError: c.certificateGenerationError || undefined,
        certificateDocumentId: c.certificateDocumentId || undefined,
        // Use glNotificationStatus from confirmed if available (may indicate NotApplicable)
        glNotificationStatus:
          c.glNotificationStatus || (confirmed && !confirmed.glContactEmail ? 'NotApplicable' : undefined),
        glNotificationError: c.glNotificationError || undefined,
        // Use glRecipientEmail if sent, otherwise use glContactEmail from confirmed candidates
        glRecipientEmail: c.glRecipientEmail || confirmed?.glContactEmail || undefined,
        glName: confirmed?.glName || gl?.name,
        glAbbreviation: confirmed?.glAbbreviation || gl?.abbreviations?.[0],
        certificateSentOn: c.certificateSentOn ? new Date(c.certificateSentOn) : undefined,
      };
    });
  });

  hasProcessingCertificates = computed(() => {
    const licenses = this.processedLicensesForDisplay();
    return licenses.some((l) => l.certificateStatus === 'Processing' || l.certificateStatus === 'Pending');
  });

  canRetryFailures = computed(() => {
    const user = this.currentUser();
    const review = this.licenseStore.activeReview();
    // Don't allow retry actions when workflow is completed
    return (
      this.permissionService.hasEffectivePermission(user, 'Workflow.Write') && review?.currentState !== 'Completed'
    );
  });

  onRetryCertificate(candidateId: string): void {
    this.licenseStore.retryCertificateGeneration(candidateId);
  }

  onRetryGlNotification(candidateId: string): void {
    this.licenseStore.retryGlNotification(candidateId);
  }

  onViewCertificate(documentId: string): void {
    // Fetch the actual document to get its objectStoreRef
    // documentId here is the WorkflowDocument.Id (GUID), but we need the ObjectStoreRef
    this.workflowDocumentService
      .entity(documentId)
      .fetch()
      .subscribe({
        next: (response: { entity?: WorkflowDocument | null }) => {
          if (response.entity?.objectStoreRef) {
            this.documentService.viewDocument(
              response.entity.objectStoreRef,
              response.entity.fileName || 'Certificate.pdf',
              'Certificate',
              response.entity.mimeType || 'application/pdf'
            );
          } else {
            this.toastService.displayErrorMessage('Certificate document not found');
          }
        },
        error: (error: unknown) => {
          console.error('Failed to load certificate document:', error);
          this.toastService.displayErrorMessage('Failed to load certificate');
        },
      });
  }

  // Auto-refresh for pending certificates
  // Auto-refresh is now handled in the store via withHooks

  // Timeline dialog
  openTimeline(): void {
    const review = this.licenseStore.activeReview();
    if (review) {
      this.timelineRef = this.dialogService.open(RenewalTimelineDialogComponent, {
        header: 'Review Timeline & Notes',
        width: '600px',
        height: '100%',
        position: 'right',
        draggable: false,
        modal: true,
        closable: true,
        maskStyleClass: 'mask',
        contentStyle: { overflow: 'auto' },
        data: {
          reviewId: review.id,
        },
      });
    }
  }
}
